import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Menu,
  MenuItem,
  Avatar,
  FormControl,
  Select,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SecurityIcon from "@mui/icons-material/Security";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useNotify } from "react-admin";
import { rolesApi, organizationsApi } from "../../providers/env";
import { CreateRoleModal } from "../../components/CreateRoleModal";
import { EditRoleModal } from "../../components/EditRoleModal";
import { RoleOut, OrganizationOut } from "../../generated";

// Extended RoleOut type with user_count from backend
interface RoleWithUserCount extends RoleOut {
  user_count?: number;
}

interface RolesTabProps {
  organizationId: number | null;
  isOrgContext: boolean;
}

export const RolesTab: React.FC<RolesTabProps> = ({ organizationId, isOrgContext }) => {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState<RoleWithUserCount[]>([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [currentOrgId, setCurrentOrgId] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRole, setSelectedRole] = useState<RoleOut | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationOut[]>([]);

  useEffect(() => {
    checkUserRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, isOrgContext]);

  const checkUserRole = () => {
    try {
      const permissions = localStorage.getItem("permissions");
      if (permissions) {
        const userInfo = JSON.parse(permissions);
        setIsSuperAdmin(userInfo.is_superuser || false);

        if (isOrgContext && organizationId) {
          // Organization context: use provided organization ID
          setCurrentOrgId(String(organizationId));
          fetchRoles(String(organizationId));
        } else if (userInfo.is_superuser && !isOrgContext) {
          // Super admin global view: show all organizations
          fetchOrganizations();
        } else if (userInfo.organization_id) {
          // Fallback: use user's organization
          setCurrentOrgId(userInfo.organization_id);
          fetchRoles(userInfo.organization_id);
        } else {
          notify("Please select an organization first", { type: "info" });
          setLoading(false);
        }
      }
    } catch (error) {
      console.error("Error checking user role", error);
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await organizationsApi.getOrganizations();
      const orgs = response.data || [];
      setOrganizations(orgs);
      // Default to "all" to show all roles
      setCurrentOrgId("all");
      fetchRoles("all");
    } catch (error: any) {
      notify("Error loading organizations", { type: "error" });
      setLoading(false);
    }
  };

  const handleOrgChange = (orgId: string) => {
    setCurrentOrgId(orgId);
    fetchRoles(orgId);
  };

  const fetchRoles = async (orgId: string) => {
    try {
      setLoading(true);
      // If "all" is selected, fetch all roles; otherwise fetch for specific org
      const params = orgId && orgId !== "all" ? { organizationId: orgId } : {};
      const response = await rolesApi.getRoles(params);
      setRoles(response.data || []);
    } catch (error: any) {
      notify("Error loading roles", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    role: RoleOut
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    handleMenuClose();
    setOpenEdit(true);
  };

  const handleDeleteClick = () => {
    handleMenuClose();
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedRole) return;

    setDeleting(true);
    try {
      await rolesApi.deleteRole({ roleId: selectedRole.id });
      notify("Role deleted successfully", { type: "success" });
      setOpenDeleteDialog(false);
      setSelectedRole(null);
      fetchRoles(currentOrgId);
    } catch (error: any) {
      notify(error?.response?.data?.detail || "Error deleting role", {
        type: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setSelectedRole(null);
  };

  const handleEditClose = () => {
    setOpenEdit(false);
    setSelectedRole(null);
  };

  const getLevelChip = (rawLevel: string) => {
    const userLevel = rawLevel?.toLowerCase();
    if (userLevel === "super_admin") {
      return (
        <Chip
          label="Org Admin"
          size="small"
          sx={{
            backgroundColor: "#9c27b0",
            color: "white",
            fontWeight: 500,
            height: 24,
            "& .MuiChip-label": {
              px: 1,
              fontSize: "0.75rem"
            }
          }}
        />
      );
    } else if (userLevel === "sub_admin") {
      return (
        <Chip
          label="Sub Admin"
          size="small"
          sx={{
            backgroundColor: "#03a9f4",
            color: "white",
            fontWeight: 500,
            height: 24,
            "& .MuiChip-label": {
              px: 1,
              fontSize: "0.75rem"
            }
          }}
        />
      );
    } else {
      return (
        <Chip
          label="User"
          size="small"
          sx={{
            backgroundColor: "#9e9e9e",
            color: "white",
            fontWeight: 500,
            height: 24,
            "& .MuiChip-label": {
              px: 1,
              fontSize: "0.75rem"
            }
          }}
        />
      );
    }
  };

  const getPermissionChips = (role: RoleOut) => {
    if (!role.permissions || role.permissions.length === 0) {
      return <Typography variant="caption" color="text.secondary">No permissions</Typography>;
    }

    const grouped = role.permissions.reduce((acc, perm) => {
      const resource = perm.resource || "";
      if (!acc[resource]) {
        acc[resource] = 0;
      }
      acc[resource]++;
      return acc;
    }, {} as Record<string, number>);

    const formatResourceName = (resource: string) => {
      return resource
        .split("_")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    };

    return (
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        {Object.entries(grouped).map(([resource, count]) => (
          <Chip
            key={resource}
            label={`${formatResourceName(resource)} (${count})`}
            size="small"
            variant="outlined"
            sx={{
              borderColor: "#e0e0e0",
              backgroundColor: "#fafafa",
              color: "#616161",
              fontSize: "0.75rem",
              height: 24,
              "& .MuiChip-label": {
                px: 1
              }
            }}
          />
        ))}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography>Loading roles...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isSuperAdmin && organizations.length > 0 && (
            <FormControl sx={{ minWidth: 250 }} size="small">
              <InputLabel>Organization</InputLabel>
              <Select
                value={currentOrgId}
                label="Organization"
                onChange={(e) => handleOrgChange(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: "0.875rem", fontWeight: 500 }}
          >
            {roles.length} total roles
          </Typography>
        </Box>
        <Tooltip
          title={(!currentOrgId || currentOrgId === "all") ? "Please select an organization first" : ""}
          arrow
          placement="left"
        >
          <span>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                if (!currentOrgId || currentOrgId === "all") {
                  notify("Please select a specific organization to create a role", { type: "warning" });
                  return;
                }
                setOpenCreate(true);
              }}
              disabled={!currentOrgId || currentOrgId === "all"}
              sx={{
                backgroundColor: "#1a1a1a",
                color: "white",
                textTransform: "none",
                fontWeight: 500,
                px: 2,
                py: 1,
                "&:hover": {
                  backgroundColor: "#333333",
                },
                "&:disabled": {
                  backgroundColor: "#9e9e9e",
                  color: "white"
                }
              }}
            >
              Create Role
            </Button>
          </span>
        </Tooltip>
      </Box>

      <TableContainer 
        component={Paper}
        sx={{
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: 2,
          overflow: "hidden"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#fafafa" }}>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>Role Name</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>Level</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>Description</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>Permissions</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>Users</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: "0.875rem" }}>Created</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow 
                key={role.id}
                sx={{
                  "&:hover": {
                    backgroundColor: "#f9f9f9"
                  }
                }}
              >
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: role.user_level?.toLowerCase() === "super_admin"
                          ? "#9c27b0"
                          : role.user_level?.toLowerCase() === "sub_admin"
                          ? "#03a9f4"
                          : "#9e9e9e",
                        fontSize: "0.875rem"
                      }}
                    >
                      <SecurityIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {role.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{getLevelChip(role.user_level || "user")}</TableCell>
                <TableCell>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      maxWidth: 300,
                      color: "#616161",
                      fontSize: "0.875rem"
                    }}
                  >
                    {role.description || "-"}
                  </Typography>
                </TableCell>
                <TableCell>{getPermissionChips(role)}</TableCell>
                <TableCell>
                  <Typography 
                    variant="body2"
                    sx={{ 
                      color: "#616161",
                      fontSize: "0.875rem"
                    }}
                  >
                    {(role as any).user_count ?? (role as any).users_count ?? 0}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography 
                    variant="body2"
                    sx={{ 
                      color: "#616161",
                      fontSize: "0.875rem"
                    }}
                  >
                    {role.created_at 
                      ? new Date(role.created_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric"
                        })
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, role)}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f0f0f0"
                      }
                    }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem 
          onClick={handleDeleteClick}
          disabled={selectedRole?.is_system_role && !isSuperAdmin}
          sx={{
            color: (selectedRole?.is_system_role && !isSuperAdmin) ? "text.disabled" : "error.main",
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create Role Modal */}
      {currentOrgId && currentOrgId !== "all" && (
        <CreateRoleModal
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onSuccess={() => fetchRoles(currentOrgId)}
          organizationId={currentOrgId}
        />
      )}

      {/* Edit Role Modal */}
      <EditRoleModal
        open={openEdit}
        onClose={handleEditClose}
        onSuccess={() => fetchRoles(currentOrgId)}
        role={selectedRole}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: "400px",
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Delete Role
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the role <strong>"{selectedRole?.name}"</strong>?
            This action cannot be undone.
            {selectedRole?.is_system_role && !isSuperAdmin && (
              <Typography color="error" sx={{ mt: 2 }}>
                System roles cannot be deleted.
              </Typography>
            )}
            {selectedRole?.is_system_role && isSuperAdmin && (
              <Typography color="warning.main" sx={{ mt: 2 }}>
                Warning: This is a system role. Deleting it may affect organization functionality.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={handleDeleteCancel} variant="outlined" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={deleting || (selectedRole?.is_system_role && !isSuperAdmin)}
            startIcon={
              deleting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};